import { Box, Typography } from '@mui/material';
import PlusIcon from 'assets/add-white.svg';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomSearch from 'components/shared/CustomSearch/CustomSearch';
import CustomTab from 'components/shared/CustomTab/CustomTab';
import { IOSSwitch } from 'components/shared/IOSSwitch';
import { useEffect, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { modalReducer } from 'reducers/modalReducer';
import { DealSelectionModal } from '../DealSelectionModal';
import { FilterBadge } from './components/FilterBadge';
import { FilterPopover } from './components/FilterPopover';
import { LayoutSwitcher } from './components/LayoutSwitcher';
import { PipelineFilter } from './components/PipelineFilter';
import styles from './DealsFilters.module.css';
import { DealsFiltersProps } from './DealsFilters.types';
import { calculateActiveFilterCount, createInitialFilterState } from './DealsFilters.utils';
import { useClientOptions } from './hooks/useClientOptions';
import { useOwnerOptions } from './hooks/useOwnerOptions';
import { usePipelineOptions } from './hooks/usePipelineOptions';

export const DealsFilters = ({
  layout,
  onLayoutChange,
  filterState,
  onFilterChange,
  selectedPipelines,
  onPipelinesChange,
  tabValue,
  onTabChange,
  searchTerm = '',
  onSearchChange,
  needsAttention = false,
  onNeedsAttentionChange,
  isPipelineVisible = true,
}: DealsFiltersProps) => {
  const [filterPopoverAnchor, setFilterPopoverAnchor] = useState<HTMLButtonElement | null>(null);
  const location = useLocation();
  const modal = location.state?.modal;

  useEffect(() => {
    if (modal) {
      dispatch({ type: 'OPEN', payload: 'typeSelect' });
    }
  }, [modal]);

  const [modalState, dispatch] = useReducer(modalReducer, {
    typeSelect: false,
  });

  const { pipelineOptions } = usePipelineOptions();
  const { ownerOptions, loading: ownerLoading, totalLength: ownerTotalLength } = useOwnerOptions();
  const {
    clientOptions,
    loading: clientLoading,
    totalLength: clientTotalLength,
    fetchClients,
  } = useClientOptions();

  const tabs = [
    {
      label: 'My Deals',
      id: 1,
      isHidden: tabValue !== 0,
      Component: <></>,
    },
    {
      label: 'All Deals',
      id: 2,
      isHidden: tabValue !== 1,
      Component: <></>,
    },
  ];

  const handleSearchDeals = (term: string) => {
    onSearchChange?.(term);
  };

  const handleNeedsAttentionSwitch = () => {
    onNeedsAttentionChange?.(!needsAttention);
  };

  const clearAllFilters = () => {
    onFilterChange(createInitialFilterState());
  };

  const handleFilterButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterPopoverAnchor(event.currentTarget);
  };

  const handleFilterPopoverClose = () => {
    setFilterPopoverAnchor(null);
  };

  const handleOwnerSearch = (value: string) => {};

  const handleClientSearch = (value: string) => {
    fetchClients(value);
  };

  const activeFilterCount = calculateActiveFilterCount(filterState);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <CustomTab tabs={tabs} handleChange={onTabChange} value={tabValue} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          gap: { xs: '8px', sm: '12px' },
          alignItems: 'center',
        }}
      >
        <Box sx={{ width: { xl: '240px', xs: '150px' } }}>
          <CustomSearch getProjects={handleSearchDeals} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '12px', sm: '14px' },
              whiteSpace: 'nowrap',
            }}
          >
            Needs Attention
          </Typography>
          <IOSSwitch checked={needsAttention} onChange={handleNeedsAttentionSwitch} />
        </Box>

        {isPipelineVisible && (
          <PipelineFilter
            selectedPipelines={selectedPipelines}
            pipelineOptions={pipelineOptions}
            onChange={onPipelinesChange}
          />
        )}

        <FilterBadge activeFilterCount={activeFilterCount} onClick={handleFilterButtonClick} />

        <LayoutSwitcher layout={layout} onLayoutChange={onLayoutChange} />

        <CustomButton
          disabled={false}
          variant='contained'
          text='New Deal'
          onClick={() => {
            dispatch({ type: 'OPEN', payload: 'typeSelect' });
          }}
          icon={<img src={PlusIcon} className={styles['add-icon']} alt='white arrow' />}
          iconPosition='start'
          style={{
            borderRadius: { xs: '10px', xl: '14px' },
            height: { xl: 48 },
            padding: { xs: '10px 12px', xl: '14px 16px' },
          }}
          buttonVariant='responsiveButtonMedium'
        />

        <DealSelectionModal isModalOpen={modalState.typeSelect} modalDispatch={dispatch} />

        <FilterPopover
          anchorEl={filterPopoverAnchor}
          onClose={handleFilterPopoverClose}
          filterState={filterState}
          onFilterChange={onFilterChange}
          onClearAll={clearAllFilters}
          ownerOptions={ownerOptions}
          clientOptions={clientOptions}
          ownerLoading={ownerLoading}
          clientLoading={clientLoading}
          ownerTotalLength={ownerTotalLength}
          clientTotalLength={clientTotalLength}
          onOwnerSearch={handleOwnerSearch}
          onClientSearch={handleClientSearch}
        />
      </Box>
    </Box>
  );
};
